/** Shared types, constants, and helpers for AllLogs and MyLogs */

export interface LogEntry {
  _id: string;
  userId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    employeeId?: string;
  };
  action: string;
  entity: string;
  entityId?: string;
  details?: string | object;
  createdAt: string;
}

export interface LogsResponse {
  data: LogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FilterForm {
  dateFrom?: string;
  dateTo?: string;
  action?: string;
  entity?: string;
  userId?: string;
}

export const ACTION_OPTIONS = [
  "ADD_PATIENT",
  "UPDATE_PATIENT",
  "CREATE_TREATMENT",
  "DELETE_TREATMENT",
  "EDIT_PAYMENT",
  "DELETE_PAYMENT",
  "UPDATE_TREATMENT",
  "DELETE_PATIENT",
  "ADD_DIAGNOSE",
  "REMOVE_DIAGNOSE",
  "ADD_TREATMENT_ITEM",
  "REMOVE_TREATMENT_ITEM",
  "UPDATE_ROOT_DIAGNOSES",
  "UPDATE_ROOT_VARIANT_DIAGNOSES",
  "UPDATE_ADVANCED_DIAGNOSE_NOTE",
  "ADD_EMPLOYEE",
  "UPDATE_EMPLOYEE",
  "ADD_ROLE",
  "UPDATE_ROLE",
];

export const ENTITY_OPTIONS = [
  "patient",
  "treatment",
  "payment",
  "appointment",
  "employee",
  "role",
  "diagnose",
  "product",
  "purchase",
  "brand",
  "category",
  "purpose",
  "drugs",
];

export const ACTION_LABELS: Record<string, string> = {
  ADD_PATIENT: "Added patient",
  UPDATE_PATIENT: "Updated patient",
  CREATE_TREATMENT: "Created treatment",
  DELETE_TREATMENT: "Deleted treatment",
  EDIT_PAYMENT: "Edited payment",
  DELETE_PAYMENT: "Deleted payment",
  UPDATE_TREATMENT: "Updated treatment",
  DELETE_PATIENT: "Deleted patient",
  ADD_DIAGNOSE: "Added diagnosis",
  REMOVE_DIAGNOSE: "Removed diagnosis",
  ADD_TREATMENT_ITEM: "Added treatment item",
  REMOVE_TREATMENT_ITEM: "Removed treatment item",
  UPDATE_ROOT_DIAGNOSES: "Updated root (left/right/hide)",
  UPDATE_ROOT_VARIANT_DIAGNOSES: "Updated root treatment (e.g. Root Canal)",
  UPDATE_ADVANCED_DIAGNOSE_NOTE: "Updated tooth note",
  ADD_EMPLOYEE: "Added employee",
  UPDATE_EMPLOYEE: "Updated employee",
  ADD_ROLE: "Added role",
  UPDATE_ROLE: "Updated role",
  CREATE_PRODUCT: "Created product",
  UPDATE_PRODUCT: "Updated product",
  DELETE_PRODUCT: "Deleted product",
  UPDATE_PRODUCT_PRICE: "Updated product price",
  CREATE_CATEGORY: "Created category",
  UPDATE_CATEGORY: "Updated category",
  DELETE_CATEGORY: "Deleted category",
  CREATE_BRAND: "Created brand",
  UPDATE_BRAND: "Updated brand",
  DELETE_BRAND: "Deleted brand",
  CREATE_SUPPLIER: "Created supplier",
  UPDATE_SUPPLIER: "Updated supplier",
  DELETE_SUPPLIER: "Deleted supplier",
  CREATE_PURCHASE: "Created purchase",
  UPDATE_PURCHASE: "Updated purchase",
  RELEASE_TO_PATIENT: "Released products to patient",
  RELEASE_TO_EMPLOYEE: "Released products to employee",
  CREATE_SCALE: "Created scale",
  UPDATE_SCALE: "Updated scale",
  DELETE_SCALE: "Deleted scale",
};

export const ENTITY_LABELS: Record<string, string> = {
  patient: "Patient",
  treatment: "Treatment",
  payment: "Payment",
  appointment: "Appointment",
  employee: "Employee",
  role: "Role",
  diagnose: "Diagnosis",
  product: "Product",
  purchase: "Purchase",
  brand: "Brand",
  category: "Category",
  purpose: "Purpose",
  drugs: "Drugs",
  supplier: "Supplier",
  scale: "Scale",
  release: "Release",
};

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] || action.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getEntityLabel(entity: string): string {
  return ENTITY_LABELS[entity] || entity;
}

export function formatDetailsReadable(details: string | object | undefined): string {
  if (details == null || details === "") return "—";
  if (typeof details === "string") return details.trim();
  if (typeof details !== "object") return String(details);
  const d = details as Record<string, unknown>;
  const parts: string[] = [];
  if (d.patientName != null && String(d.patientName).trim()) parts.push(`Patient: ${String(d.patientName).trim()}`);
  if (d.invoiceNo != null && String(d.invoiceNo).trim()) parts.push(`invoiceNo: ${String(d.invoiceNo).trim()}`);
  if (d.regNo != null && String(d.regNo).trim()) parts.push(`Reg No: ${String(d.regNo).trim()}`);
  if (d.method != null && String(d.method).trim()) parts.push(`Method: ${String(d.method).trim()}`);
  if (d.employeeName != null && String(d.employeeName).trim()) parts.push(`Employee: ${String(d.employeeName).trim()}`);
  if (Array.isArray(d.updatedFields) && d.updatedFields.length) {
    parts.push(`Updated: ${(d.updatedFields as string[]).join(", ")}`);
  }
  if (d.productName != null && String(d.productName).trim()) parts.push(`product: ${String(d.productName).trim()}`);
  if (d.categoryName != null && String(d.categoryName).trim()) parts.push(`category: ${String(d.categoryName).trim()}`);
  if (d.brandName != null && String(d.brandName).trim()) parts.push(`brand: ${String(d.brandName).trim()}`);
  if (d.supplierName != null && String(d.supplierName).trim()) parts.push(`supplier: ${String(d.supplierName).trim()}`);
  if (d.netTotal != null) parts.push(`netTotal: ${d.netTotal}`);
  if (d.total != null) parts.push(`total: ${d.total}`);
  if (d.scale != null && String(d.scale).trim()) parts.push(`scale: ${String(d.scale).trim()}`);
  if (d.unit != null && String(d.unit).trim()) parts.push(`unit: ${String(d.unit).trim()}`);
  if (d.newPrice != null) parts.push(`newPrice: ${d.newPrice}`);
  if (d.alertQuantity != null) parts.push(`alertQuantity: ${d.alertQuantity}`);
  if (d.addedPayments != null) parts.push(`addedPayments: ${d.addedPayments}`);
  if (d.addedProductLines != null) parts.push(`addedProductLines: ${d.addedProductLines}`);
  if (d.type != null && String(d.type).trim()) parts.push(`type: ${String(d.type).trim()}`);
  if (d.suppliercompany != null && String(d.suppliercompany).trim()) parts.push(`company: ${String(d.suppliercompany).trim()}`);
  const removedItem = d.removedItem as Record<string, unknown> | undefined;
  if (removedItem && typeof removedItem === "object" && !Array.isArray(removedItem)) {
    const itemParts: string[] = [];
    if (removedItem.treatment != null) itemParts.push(`treatment: ${removedItem.treatment}`);
    if (removedItem.price != null) itemParts.push(`price: ${removedItem.price}`);
    if (removedItem.toothNo != null) itemParts.push(`toothNo: ${removedItem.toothNo}`);
    if (removedItem.type != null) itemParts.push(`type: ${removedItem.type}`);
    if (removedItem.subtype != null) itemParts.push(`subtype: ${removedItem.subtype}`);
    if (itemParts.length) parts.push(`removedItem: ${itemParts.join(", ")}`);
  }
  const knownKeys = ["patientName", "invoiceNo", "regNo", "method", "employeeName", "updatedFields", "removedItem", "productName", "categoryName", "brandName", "supplierName", "netTotal", "total", "scale", "unit", "newPrice", "alertQuantity", "addedPayments", "addedProductLines", "type", "suppliercompany"];
  for (const key of Object.keys(d)) {
    if (knownKeys.includes(key)) continue;
    const v = d[key];
    if (v == null) continue;
    const str = typeof v === "object" ? JSON.stringify(v) : String(v).trim();
    if (str) parts.push(`${key}: ${str}`);
  }
  return parts.length ? parts.join(", ") : JSON.stringify(details);
}

export function getReadableDescription(entry: LogEntry): string {
  const actionText = getActionLabel(entry.action);
  const entityText = getEntityLabel(entry.entity);
  const detailStr = formatDetailsReadable(entry.details);
  if (detailStr && detailStr !== "—") {
    return `${actionText} (${entityText}) — ${detailStr}`;
  }
  return `${actionText} (${entityText})`;
}

export function formatDetails(details: string | object | undefined): string {
  const str = formatDetailsReadable(details);
  return str.length > 100 ? str.slice(0, 97) + "…" : str;
}

export function userName(entry: LogEntry): string {
  const u = entry.userId;
  if (!u) return "-";
  const first = u.firstName || "";
  const last = u.lastName || "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || u.employeeId || entry.userId?._id || "-";
}
